/**
 * @fileoverview Sidebar para selección de ubicación en Google Maps con marker arrastrable y geolocalización.
 * Permite al usuario seleccionar una ubicación en el mapa, mover el marker y obtener la localización actual si el navegador lo soporta.
 * Integración completa con i18n para textos y errores, siguiendo arquitectura optimizada y mejores prácticas de performance.
 *
 * @module LocationPickerSidebar
 * @category Component - Organism
 * @since 1.0.0
 * @version 3.0.0
 *
 * @typedef {Object} Location
 * @description Objeto que representa una ubicación geográfica seleccionada en el mapa.
 * @property {number} lat - Latitud de la ubicación seleccionada
 * @property {number} lng - Longitud de la ubicación seleccionada
 *
 * @param {Object} props - Propiedades del componente
 * @param {function} props.handleSubmit - Función para enviar la ubicación seleccionada
 * @param {number} [props.zoomLevel=17] - Nivel de zoom inicial del mapa
 * @param {Location} [props.defaultLocation] - Ubicación inicial por defecto
 *
 * @returns {JSX.Element} Sidebar con mapa y marker seleccionable
 *
 * @example
 * <LocationPickerSidebar handleSubmit={fn} />
 *
 * @see {@link GoogleMap} Componente de mapa de Google
 * @see {@link Marker} Marker arrastrable para selección
 * @see {@link LoadingMC} Loader mientras carga el mapa
 * @see {@link RightModalHeader} Header del modal
 *
 * @architecture
 * - Integración con i18n: Todos los textos y errores usan claves de traducción (location_picker_sidebar)
 * - Performance: Memoización de funciones y props con useCallback y useMemo
 * - Limpieza de efectos: Manejo robusto de ciclo de vida y cleanup
 * - Separación de responsabilidades: Sin lógica de negocio ni UI mezclada
 *
 * @performance
 * - Evita re-renders innecesarios en mapa y marker
 * - Loader optimizado para carga de mapa
 * - Manejo eficiente de errores y estados
 */
import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { GoogleMap, Marker, Autocomplete, useJsApiLoader } from '@react-google-maps/api';
import { SIDEBAR_ACTIONS } from '../../constants/actionsConstants';
import RightModalHeader from '../layouts/Modal/RightModalHeader';
import LoadingMC from '../components/LoadingMC';
import ButtonComponent from '../components/ButtonMC';

const DEFAULT_LOCATION = { lat: -0.190599, lng: -78.485619 };

const LocationPickerSidebar = memo(({
  handleSubmit,
  zoomLevel = 17,
  defaultLocation = DEFAULT_LOCATION
}) => {
  const { t } = useTranslation();
  // Memoización de estilos para evitar recalculo
  const mapStyles = useMemo(() => ({ width: '100%', height: '100%' }), []);
  const dispatch = useDispatch();
  // Memoización de location inicial para evitar re-renders innecesarios
  const [location, setLocation] = useState(() => defaultLocation);
  const [loadingMap, setLoadingMap] = useState(true);
  const [autocomplete, setAutocomplete] = useState(null);
  const inputRef = useRef(null);
  // Carga robusta de Google Maps API
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: ENV.GOOGLE_API_KEY,
    libraries: ['places']
  });

  /**
   * Maneja el evento de carga del Autocomplete.
   * @function onAutocompleteLoad
   * @memberof LocationPickerSidebar
   * @param {Object} ac - Instancia de Autocomplete
   */
  const onAutocompleteLoad = useCallback((ac) => {
    if (ac && typeof ac.getPlace === 'function') {
      setAutocomplete(ac);
    }
  }, []);

  /**
   * Maneja el evento de selección en Autocomplete, extrayendo dirección y coordenadas.
   * @function onPlaceChanged
   * @memberof LocationPickerSidebar
   */
  const onPlaceChanged = useCallback(() => {
    if (!autocomplete || typeof autocomplete.getPlace !== 'function') return;
    
    try {
      const place = autocomplete.getPlace();
      if (!place || !place.geometry || !place.geometry.location) return;
      
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      // Extraer dirección legible
      const address = place.formatted_address || (place.name ? place.name : '');
      setLocation({ lat, lng, address });
    } catch (error) {
      console.error('Error al obtener el lugar del Autocomplete:', error);
    }
  }, [autocomplete]);

  // Obtener la localización actual del usuario al montar
  // Efecto para obtener la localización actual del usuario al montar, con cleanup robusto
  React.useEffect(() => {
    let isMounted = true;

    // Solo obtener geolocalización si NO hay una ubicación por defecto válida
    const hasValidDefaultLocation = defaultLocation && defaultLocation.lat && defaultLocation.lng
    && (defaultLocation.lat !== DEFAULT_LOCATION.lat || defaultLocation.lng !== DEFAULT_LOCATION.lng);

    if (hasValidDefaultLocation) {
      // Si ya hay una ubicación válida, no solicitar geolocalización
      return () => { isMounted = false; };
    }

    if (!('geolocation' in navigator)) {
      console.error(t('location_picker_sidebar.error.geolocation_not_supported'));
      return () => { isMounted = false; };
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (!isMounted) return;
        const { latitude, longitude } = pos.coords;
        setLocation({ lat: latitude, lng: longitude });
      },
      (err) => {
        if (!isMounted) return;
        console.error(t('location_picker_sidebar.error.geolocation_failed'));
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
    return () => { isMounted = false; };
  }, [t, defaultLocation]);

  // Cleanup para autocomplete
  React.useEffect(() => {
    return () => {
      setAutocomplete(null);
    };
  }, []);

  /**
   * Cierra el sidebar de selección de ubicación.
   *
   * @function handleClickOnClose
   * @memberof LocationPickerSidebar
   * @description
   * Dispara la acción de cierre del sidebar usando Redux.
   *
   * @example
   * handleClickOnClose();
   */
  const handleClickOnClose = useCallback(() => {
    dispatch({
      type: SIDEBAR_ACTIONS.CLOSE_SIDEBAR,
      payload: {
        componentKey: 'LocationPickerSidebar',
      }
    });
  }, [dispatch]);

  /**
   * Callback que se ejecuta cuando el mapa termina de cargar.
   *
   * @function onMapLoad
   * @memberof LocationPickerSidebar
   * @description
   * Actualiza el estado para ocultar el loader.
   *
   * @example
   * onMapLoad();
   */
  const onMapLoad = useCallback(() => {
    setLoadingMap(false);
  }, []);

  /**
   * Maneja el evento de click en el mapa para seleccionar una nueva ubicación.
   *
   * @function handleMapClick
   * @memberof LocationPickerSidebar
   * @param {Object} e - Evento de Google Maps
   * @description
   * Actualiza el estado de la ubicación con las coordenadas seleccionadas.
   *
   * @example
   * handleMapClick(e);
   */
  const handleMapClick = useCallback((e) => {
    if (!e || !e.latLng) return;
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    setLocation({ lat, lng });
  }, []);

  /**
   * Maneja el evento de arrastre del marker para actualizar la ubicación.
   *
   * @function handleMarkerDragEnd
   * @memberof LocationPickerSidebar
   * @param {Object} e - Evento de Google Maps
   * @description
   * Actualiza el estado de la ubicación con las nuevas coordenadas del marker.
   *
   * @example
   * handleMarkerDragEnd(e);
   */
  const handleMarkerDragEnd = useCallback((e) => {
    if (!e || !e.latLng) return;
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    setLocation({ lat, lng });
  }, []);

  /**
   * Envía la ubicación seleccionada, incluyendo la dirección si existe, o '' si no existe.
   * @function handleSend
   * @memberof LocationPickerSidebar
   */
  const handleSend = useCallback(() => {
    const { lat, lng, address } = location || {};
    handleSubmit({
      latitude: lat,
      longitude: lng,
      address: address || ''
    });
    handleClickOnClose();
  }, [handleSubmit, location, handleClickOnClose]);

  // Memoización de props para GoogleMap y Marker
  const googleMapProps = useMemo(() => ({
    onLoad: onMapLoad,
    mapContainerStyle: mapStyles,
    center: location,
    zoom: zoomLevel,
    onClick: handleMapClick,
    options: { fullscreenControl: false }
  }), [onMapLoad, mapStyles, location, zoomLevel, handleMapClick]);

  const markerProps = useMemo(() => ({
    position: location,
    draggable: true,
    onDragEnd: handleMarkerDragEnd
  }), [location, handleMarkerDragEnd]);

  // Renderiza el contenido del mapa solo si Google Maps está cargado
  const MapContent = (
    <>
      {loadingMap && (
        <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-white/60 tw-z-10">
          <LoadingMC loading={loadingMap} />
        </div>
      )}
      <div className="tw-absolute tw-top-[10px] tw-right-4 tw-z-[10010] tw-w-[340px] md:tw-w-[400px]" style={{ minWidth: 200, maxWidth: 400 }}>
        <Autocomplete
          onLoad={onAutocompleteLoad}
          onPlaceChanged={onPlaceChanged}
        >
          <input
            ref={inputRef}
            id="location-autocomplete-input"
            type="text"
            autoComplete="off"
            placeholder={t('location_picker_sidebar.input.placeholder')}
            className="tw-bg-white tw-px-4 tw-py-2 tw-rounded tw-shadow tw-w-full tw-border tw-border-gray-300 tw-text-default tw-font-sans"
            style={{ zIndex: 10010 }}
          />
        </Autocomplete>
      </div>
      <GoogleMap {...googleMapProps}>
        <Marker {...markerProps} />
      </GoogleMap>
    </>
  );

  return (
    <div className="tw-wrapper tw-flex tw-flex-col">
      <RightModalHeader
        title={t('location_picker_sidebar.header.title')}
        caption={t('location_picker_sidebar.header.caption')}
        onClose={handleClickOnClose}
      />
      <div className="tw-p-4 tw-w-full">
        <div className="tw-w-full tw-h-[40vh] md:tw-h-[400px] tw-relative">
          {!isLoaded ? (
            <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-white/60 tw-z-10">
              <LoadingMC loading={true} />
            </div>
          ) : (
            MapContent
          )}
        </div>
        <hr className="tw-my-4 tw-border-gray-200" />
        <div className="tw-flex tw-justify-end">
          <ButtonComponent
            text={t('location_picker_sidebar.button.send')}
            onClick={handleSend}
            size="medium"
          />
        </div>
      </div>
    </div>
  );
});

LocationPickerSidebar.displayName = 'LocationPickerSidebar';

export default LocationPickerSidebar;
